import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from '@/components/ui/drawer'
import { X, Maximize2, Minimize2 } from 'lucide-react'
import { useContext, useEffect, useState } from 'react'
import { GlobalContext, type GlobalContextType } from '@/store/GlobalContext'
import { type Cat } from '@/type/cat'
import { Button } from '@/components/ui/button'
import { Cat as CatIcon } from 'lucide-react'

const PlaceDetails = (props: {
  place: { lat: number; lng: number }
  onClose: () => void
  onCatSelect?: (cat: Cat) => void
  onFoundNewCat?: () => void
}) => {
  const { place, onClose, onCatSelect, onFoundNewCat } = props

  const { cats } = useContext(GlobalContext) as GlobalContextType
  const [address, setAddress] = useState<string>('')
  const [isLoadingAddress, setIsLoadingAddress] = useState(true)
  const [isOpen, setIsOpen] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    setIsOpen(!!place)
  }, [place])

  useEffect(() => {
    const geocoder = new google.maps.Geocoder()

    geocoder.geocode(
      { location: { lat: place.lat, lng: place.lng } },
      (results, status) => {
        if (status === 'OK' && results?.[0]) {
          setAddress(results[0].formatted_address)
        } else {
          setAddress(`${place.lat.toFixed(6)}, ${place.lng.toFixed(6)}`)
        }
        setIsLoadingAddress(false)
      }
    )
  }, [place])

  // Calculate distance between two points using Haversine formula
  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ) => {
    const R = 6371 // Radius of the earth in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180
    const dLon = ((lon2 - lon1) * Math.PI) / 180
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c // Distance in km
  }

  // Find nearby cats within 1km radius
  const nearbyCats = cats.data.filter((cat) => {
    const distance = calculateDistance(
      place.lat,
      place.lng,
      cat.location.lat,
      cat.location.lng
    )
    return distance <= 0.5 // 0.5km radius
  })

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) {
          setTimeout(() => {
            onClose()
          }, 300)
        }
      }}
    >
      <DrawerContent
        className={`${
          isExpanded ? 'h-[100vh]' : 'h-[65vh]'
        } overflow-hidden transition-all duration-300`}
      >
        <div className='mx-auto w-full max-w-2xl flex flex-col h-full overflow-y-auto'>
          <DrawerHeader className='flex-none px-4'>
            {/* starts:: header */}
            <DrawerTitle>
              <div className='flex items-start justify-between'>
                <div className='flex flex-col gap-2 pr-8'>
                  {isLoadingAddress ? (
                    <div className='animate-pulse'>
                      <div className='h-8 w-64 bg-gray-200 rounded mb-2'></div>
                      <div className='h-8 w-48 bg-gray-200 rounded'></div>
                    </div>
                  ) : (
                    <>
                      <DrawerTitle
                        className='text-2xl font-semibold text-start overflow-hidden text-ellipsis'
                        style={{
                          maxHeight: '4rem',
                          display: '-webkit-box',
                          WebkitLineClamp: '2',
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {address}
                      </DrawerTitle>
                      <DrawerDescription className='sr-only'>
                        Location details for {address}
                      </DrawerDescription>
                    </>
                  )}
                </div>
                <div className='flex gap-3'>
                  <Button
                    variant='ghost'
                    size='icon'
                    className='h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200'
                    onClick={() => setIsExpanded(!isExpanded)}
                  >
                    {isExpanded ? (
                      <Minimize2 className='h-4 w-4 text-gray-600' />
                    ) : (
                      <Maximize2 className='h-4 w-4 text-gray-600' />
                    )}
                  </Button>
                  <Button
                    variant='ghost'
                    size='icon'
                    className='h-8 w-8 rounded-full bg-gray-100 hover:bg-gray-200'
                    onClick={onClose}
                  >
                    <X className='h-4 w-4 text-gray-600' />
                  </Button>
                </div>
              </div>
            </DrawerTitle>
            {/* ends:: header */}

            <DrawerDescription>
              <Button
                variant='primary'
                className='w-full rounded-full shadow shadow-orange-600 mt-4 py-6 text-md flex items-center justify-center'
                onClick={onFoundNewCat}
              >
                I found a new cat
                <CatIcon className='h-16 w-16' />
              </Button>
            </DrawerDescription>

            {/* starts:: nearby cats */}
            {nearbyCats.length > 0 && (
              <div className='mt-6'>
                <h2 className='text-start text-gray-500 font-medium mb-2'>
                  Near this address
                </h2>
                <div className='flex flex-col gap-3'>
                  {nearbyCats.map((cat: Cat) => (
                    <div
                      key={cat.id}
                      className='flex items-center gap-4 p-3 bg-white rounded-2xl border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors'
                      onClick={() => {
                        onClose()
                        onCatSelect?.(cat)
                      }}
                    >
                      <img
                        src={cat.images[0]}
                        alt={cat.name}
                        className='h-16 w-16 rounded-xl'
                      />
                      <div className='flex flex-col'>
                        <h3 className='font-semibold text-start'>{cat.name}</h3>
                        <p className='text-sm text-gray-600 text-start'>
                          {cat.bio}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* ends:: nearby cats */}

            {/* starts:: location details */}
            <div className='my-6'>
              <h2 className='text-start text-gray-500 font-medium mb-2'>
                Details
              </h2>
              <div className='bg-gray-100 p-4 rounded-lg'>
                <h3 className='text-gray-500 mb-2 text-start'>Address</h3>
                <div className='text-start'>
                  {isLoadingAddress ? (
                    <div className='animate-pulse'>
                      <div className='h-6 w-full bg-gray-200 rounded'></div>
                    </div>
                  ) : (
                    address
                  )}
                </div>
              </div>
            </div>
            {/* ends:: location details */}
          </DrawerHeader>
        </div>
      </DrawerContent>
    </Drawer>
  )
}

export default PlaceDetails
